/**
 * Iter92: useStickyState — persistiert Filter/View-State pro Seite in localStorage.
 *
 * Beispiel:
 *   const [filter, setFilter] = useStickyState("inbox.filter", "all");
 *
 * - Liest beim Mount aus localStorage (falls vorhanden) — sonst defaultValue.
 * - Schreibt bei jeder Änderung zurück.
 * - Namespace per Firma: `companyId` wird in den Key gehängt damit Filter
 *   nicht zwischen Mandanten geleakt werden.
 * - `useResetStickyState(prefix)` löscht alle Keys mit dem Prefix (für Reset-Button).
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const PREFIX = "view";

function buildKey(key, companyId) {
  return `${PREFIX}:${companyId || "global"}:${key}`;
}

function safeRead(fullKey, fallback) {
  try {
    const raw = window.localStorage.getItem(fullKey);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    console.warn("useStickyState parse failed for", fullKey, e);
    return fallback;
  }
}

function safeWrite(fullKey, value) {
  try {
    if (value === undefined || value === null || value === "") {
      window.localStorage.removeItem(fullKey);
    } else {
      window.localStorage.setItem(fullKey, JSON.stringify(value));
    }
  } catch (e) {
    console.warn("useStickyState write failed for", fullKey, e);
  }
}

/**
 * @param {string} key z.B. "inbox.filter"
 * @param {*} defaultValue
 * @param {string|null} companyId
 */
export function useStickyState(key, defaultValue, companyId = null) {
  const fullKey = useMemo(() => buildKey(key, companyId), [key, companyId]);
  const [value, setValue] = useState(() => safeRead(fullKey, defaultValue));

  // Wenn Company wechselt, neuen Wert aus localStorage lesen
  useEffect(() => {
    setValue(safeRead(fullKey, defaultValue));
    // eslint-disable-next-line
  }, [fullKey]);

  useEffect(() => {
    safeWrite(fullKey, value);
  }, [fullKey, value]);

  return [value, setValue];
}

/**
 * Löscht alle Sticky-States mit einem Prefix.
 * Beispiel: useResetStickyState("inbox") löscht "inbox.filter", "inbox.sort", ...
 */
export function useResetStickyState(prefixes, companyId = null) {
  const key = JSON.stringify(prefixes);
  return useCallback(() => {
    const list = Array.isArray(prefixes) ? prefixes : [prefixes];
    try {
      const fullPrefixes = list.map((p) => buildKey(p, companyId));
      const toDelete = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && fullPrefixes.some((fp) => k.startsWith(fp))) {
          toDelete.push(k);
        }
      }
      toDelete.forEach((k) => window.localStorage.removeItem(k));
    } catch (e) {
      console.warn("useResetStickyState failed", e);
    }
    // eslint-disable-next-line
  }, [key, companyId]);
}
